<template>
  <div
    v-if="!isLoading"
    class="profileCard"
    :class="`profileCard--${type || 'mini'}`"
    @mouseover="hovered = true"
    @mouseleave="hovered = false"
  >
    <figure
      class="image profileCard__avatar"
      :class="`profileCard__avatar--${type || 'mini'}`"
      style="position: relative"
    >
      <b-tooltip
        :class="allowEdit ? 'is-primary' : 'is-warning is-light'"
        :delay="200"
      >
        <a @click="handleAvatarClick">
          <AvatarImg :player="player" :isSquared="isSquared" />
        </a>

        <template v-slot:content>
          <div v-if="allowEdit">Edit avatar</div>
          <div v-else>
            <p style="min-width: 200px">
              <b>{{ playerName }}</b>
            </p>
            <p style="font-size: 0.9em; white-space: normal">
              <i>{{ schoolName }}</i>
            </p>
            <p v-if="rating">
              <i>
                Rating: <span class="icon"><i class="fab fa-jedi-order" /></span
                >{{ rating }}
              </i>
            </p>
            <p v-if="winStreak">
              <i>Winning streak: ✨{{ winStreak }}</i>
            </p>
          </div>
        </template>
      </b-tooltip>
    </figure>

    <div
      class="profileCard__data"
      :class="`profileCard__data--${type || 'mini'}`"
    >
      <!-- Player Name -->
      <div
        :class="
          type && type.startsWith('top') ? '' : 'is-flex is-align-items-center'
        "
      >
        <a
          v-if="player && player.username && !player.isAi"
          @click="gotoProfile"
        >
          <b-tooltip
            label="View Profile"
            position="is-right"
            :delay="200"
            :class="`profileCard__playerName--${type || 'mini'}`"
          >
            <b :class="`has-text-${color}`">{{ playerName }}</b>
          </b-tooltip>
        </a>
        <b v-else> {{ playerName }} </b>
        <div class="is-flex" :class="`profileCard__rating--${type || 'mini'}`">
          <PlayerRating
            :rating="rating"
            :diff="ratingDiff"
            :type="type"
            class="mr-1"
          />
          <WinStreak
            v-if="type != 'topMedium'"
            class="ml-1"
            :winStreak="winStreak"
            :type="type"
          />
        </div>
      </div>

      <!-- School Name -->
      <p
        v-if="schoolName"
        class="profileCard__schoolName"
        :class="`profileCard__schoolName--${type || 'mini'} has-text-${color}`"
      >
        {{ schoolName }}
      </p>

      <MasteryLevel v-if="level != null" :level="level" />
    </div>

    <div v-if="onlinePlayer">
      <!-- Status tag -->
      <StatusTag
        style="position: absolute; top: 0; right: 0"
        :status="onlinePlayer.status"
        :challenge="onlinePlayer.challenge"
      />

      <!-- Action Buttons: Challenge -->
      <div
        class="buttons"
        style="position: absolute; top: 0; right: 0"
        v-if="challenge"
        v-show="hovered"
      >
        <b-button
          size="is-small"
          icon-left="paper-plane"
          v-show="onlinePlayer.status === 'Active' && !onlinePlayer.challenge"
          type="is-info"
          @click="handleChallenge"
        >
          Challenge
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import * as PlayerHelper from "vue-chess-shared/utils/player-helper";
import MasteryLevel from "@/components/stats/MasteryLevel.vue";
import PlayerRating from "@/components/stats/PlayerRating.vue";
import WinStreak from "@/components/stats/WinStreak.vue";
import StatusTag from "@/components/statuses/StatusTag.vue";
import AvatarImg from "./AvatarImg.vue";
import { EventBus } from "@/services/event-bus";

export default {
  name: "ProfileCard",
  components: { MasteryLevel, PlayerRating, WinStreak, StatusTag, AvatarImg },

  props: [
    "player",
    "level",
    "type",
    "color",
    "isSquared",
    "onlinePlayer",
    "challenge",
    "allowEdit",
  ],

  data() {
    return { hovered: false };
  },

  computed: {
    isLoading() {
      return !this.player;
    },

    playerName() {
      return PlayerHelper.playerName(this.player);
    },

    schoolName() {
      if (!this.player) return null;
      if (!this.player.school) return null;
      return this.player.school.school_name;
    },

    rating() {
      if (this.player && this.player.stats) return this.player.stats.rating;
      return null;
    },

    ratingDiff() {
      if (this.player && this.player.stats) {
        return this.player.stats.rating_diff;
      }
      return null;
    },

    winStreak() {
      if (this.player && this.player.stats) return this.player.stats.win_streak;
      return null;
    },
  },

  methods: {
    handleAvatarClick() {
      if (this.allowEdit) {
        window.location =
          "https://www.chesssquad.com/pages/chess-student/avatar-select.php";
        return;
      }
      return this.gotoProfile();
    },

    gotoProfile() {
      if (!this.player || !this.player.username) return;
      if (
        this.$route.name === "profile" &&
        this.$route.query.u === this.player.username
      ) {
        return;
      }
      this.$router.push({
        name: "profile",
        query: { u: this.player.username },
      });
    },

    handleChallenge() {
      if (!this.onlinePlayer) return;
      EventBus.$emit("onChallenge", this.onlinePlayer);
    },
  },
};
</script>

<style scoped>
.profileCard {
  display: flex;
  align-items: center;
  position: relative;
}

.profileCard--top,
.profileCard--topMedium,
.profileCard--topMini {
  flex-direction: column;
}

.profileCard__avatar {
  width: 42px;
  height: 42px;
  margin-right: 5px;
}

.profileCard__avatar--medium {
  min-width: 90px;
  margin-right: 10px;
  align-self: flex-start;
}

.profileCard__avatar--large {
  min-width: 128px;
  margin-right: 10px;
  align-self: flex-start;
}

.profileCard__avatar--top {
  width: 128px;
  height: 128px;
  margin-right: 0;
  align-self: flex-start;
}

.profileCard__avatar--topMedium {
  width: 90px;
  height: 90px;
  margin-right: 0;
  align-self: flex-start;
}

.profileCard__avatar--topMini {
  width: 42px;
  height: 42px;
  margin-right: 0;
  align-self: flex-center;
}

.profileCard__data {
  text-align: left;
  display: flex;
  flex-direction: column;
  align-self: center;
}

.profileCard__data--top {
  align-self: flex-start;
  width: 128px;
}

.profileCard__data--topMedium {
  align-self: flex-start;
  width: 90px;
}

.profileCard__data--topMini {
  width: 50px;
  align-items: center;
}

.profileCard__playerName--mini {
  padding-right: 0.5rem;
}

.profileCard__playerName--long {
  padding-right: 0.5rem;
}

.profileCard__playerName--large {
  padding-right: 0.5rem;
  color: #363636;
  font-size: 1.5em;
  font-weight: 600;
  line-height: 1.125;
  white-space: nowrap;
  overflow: hidden;
  max-width: 11rem;
}

.profileCard__playerName--topMedium {
  font-size: 0.75rem;
}

.profileCard__playerName--topMini {
  font-size: 0.75rem;
  max-width: 45px;
  white-space: nowrap;
  overflow: hidden;
}

.profileCard__schoolName {
  color: #4a4a4a;
  font-style: italic;
  font-weight: 400;
  line-height: 1.25;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
}

.profileCard__schoolName--mini {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  padding-right: 5px;
}

.profileCard__schoolName--long {
  white-space: nowrap;
  max-width: 400px;
}

.profileCard__schoolName--large {
  font-size: 1.05rem;
  max-width: 270px;
}

.profileCard__schoolName--topMedium,
.profileCard__schoolName--topMini {
  display: none;
}

.profileCard__rating--topMini {
  display: none !important;
}
</style>
